import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Loader2, Download, Trash2, ExternalLink, Lock, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePerm, usePermScope } from '@/lib/perm';
import { useLang } from '@/lib/i18n';
import { api, errMsg } from '@/lib/api';
import { AppShell, PageHeader } from '@/components/AppShell';
import { ModuleIcon } from '@/components/ModuleIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const FILTERS = [
  { key: 'all', id: 'Semua', en: 'All' },
  { key: 'installed', id: 'Terinstal', en: 'Installed' },
  { key: 'available', id: 'Belum terinstal', en: 'Not installed' },
  { key: 'soon', id: 'Segera hadir', en: 'Coming soon' },
];

const ModuleCard = ({ m, lang, canEdit, busy, onInstall, onUninstall, onOpen }) => {
  const label = lang === 'id' ? m.label_id : m.label_en;
  const installed = m.available && m.enabled;
  return (
    <div className={`group relative flex flex-col rounded-2xl border border-border bg-white p-5 shadow-soft transition-shadow hover:shadow-lg ${!m.available ? 'opacity-75' : ''}`} data-testid={`module-store-card-${m.key}`}>
      <div className="flex items-start gap-4">
        <ModuleIcon moduleKey={m.key} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-semibold text-base truncate">{label}</h3>
            {m.core && <Lock size={12} className="text-muted-foreground" />}
            {installed && <Badge className="rounded-full bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-50 text-[10px] px-2" variant="outline" data-testid={`module-store-installed-${m.key}`}><CheckCircle2 size={10} className="mr-1" />{lang === 'id' ? 'Terinstal' : 'Installed'}</Badge>}
            {!m.available && <Badge variant="secondary" className="rounded-full text-[10px] px-2">{lang === 'id' ? 'Segera hadir' : 'Coming soon'}</Badge>}
          </div>
          <p className="mt-1 text-sm text-muted-foreground leading-snug">{lang === 'id' ? m.desc_id : m.desc_en}</p>
        </div>
      </div>
      <div className="mt-5 flex items-center gap-2">
        {installed && <Button size="sm" variant="outline" className="rounded-xl" onClick={() => onOpen(m)} data-testid={`module-store-open-${m.key}`}><ExternalLink size={14} className="mr-1.5" />{lang === 'id' ? 'Buka' : 'Open'}</Button>}
        {m.available && !m.enabled && (
          <Button size="sm" className="rounded-xl" disabled={!canEdit || busy === m.key} onClick={() => onInstall(m)} data-testid={`module-store-install-${m.key}`}>
            {busy === m.key ? <Loader2 size={14} className="animate-spin" /> : <><Download size={14} className="mr-1.5" />{lang === 'id' ? 'Instal' : 'Install'}</>}
          </Button>
        )}
        {installed && !m.core && canEdit && (
          <Button size="sm" variant="ghost" className="rounded-xl text-muted-foreground hover:text-destructive ml-auto" disabled={busy === m.key} onClick={() => onUninstall(m)} data-testid={`module-store-uninstall-${m.key}`}>
            {busy === m.key ? <Loader2 size={14} className="animate-spin" /> : <><Trash2 size={14} className="mr-1.5" />{lang === 'id' ? 'Hapus' : 'Uninstall'}</>}
          </Button>
        )}
      </div>
    </div>
  );
};

export default function ModulesPage() {
  const { company, refreshCompany } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const { isAdmin: canEdit } = usePerm('settings');

  const list = useMemo(() => {
    const all = company?.modules || [];
    const s = q.trim().toLowerCase();
    return all.filter((m) => {
      if (filter === 'installed' && !(m.available && m.enabled)) return false;
      if (filter === 'available' && !(m.available && !m.enabled)) return false;
      if (filter === 'soon' && m.available) return false;
      if (!s) return true;
      return [m.label_id, m.label_en, m.desc_id, m.desc_en].join(' ').toLowerCase().includes(s);
    });
  }, [company, q, filter]);

  const run = async (m, action) => {
    setBusy(m.key);
    try {
      await api.post(`/companies/${company.id}/modules/${m.key}/${action}`);
      await refreshCompany();
      const label = lang === 'id' ? m.label_id : m.label_en;
      toast.success(action === 'install' ? (lang === 'id' ? `${label} berhasil diinstal` : `${label} installed`) : (lang === 'id' ? `${label} dihapus` : `${label} uninstalled`));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  if (!company) return <AppShell><div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" /></div></AppShell>;
  const installedCount = company.modules.filter((m) => m.available && m.enabled).length;

  return (
    <AppShell moduleName={lang === 'id' ? 'Aplikasi' : 'Apps'} moduleIcon={<ModuleIcon moduleKey="apps" size="sm" />}>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <PageHeader title={lang === 'id' ? 'Aplikasi' : 'Apps'} subtitle={lang === 'id' ? `${installedCount} modul terinstal · Satu harga per pengguna mencakup semua modul.` : `${installedCount} modules installed · One per-user price covers every module.`} />
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === 'id' ? 'Cari aplikasi...' : 'Search apps...'} className="pl-9 rounded-xl bg-white" data-testid="module-store-search" />
          </div>
          <div className="flex gap-1 overflow-x-auto">
            {FILTERS.map((f) => (
              <button key={f.key} type="button" onClick={() => setFilter(f.key)} className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${filter === f.key ? 'bg-primary text-primary-foreground' : 'bg-white border border-border hover:bg-muted'}`} data-testid={`module-store-filter-${f.key}`}>{lang === 'id' ? f.id : f.en}</button>
            ))}
          </div>
        </div>
        {!canEdit && <p className="mb-4 text-xs text-muted-foreground">{lang === 'id' ? 'Hanya pemilik atau admin yang dapat menginstal/menghapus aplikasi.' : 'Only owners or admins can install/uninstall apps.'}</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="module-store-grid">
          {list.map((m) => (
            <ModuleCard key={m.key} m={m} lang={lang} canEdit={canEdit} busy={busy} onOpen={(mod) => navigate(mod.path)} onInstall={(mod) => run(mod, 'install')} onUninstall={(mod) => setConfirm(mod)} />
          ))}
          {list.length === 0 && <p className="text-sm text-muted-foreground col-span-full py-10 text-center">{lang === 'id' ? 'Tidak ada aplikasi yang cocok.' : 'No matching apps.'}</p>}
        </div>
      </div>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{lang === 'id' ? `Hapus ${confirm?.label_id}?` : `Uninstall ${confirm?.label_en}?`}</AlertDialogTitle>
            <AlertDialogDescription>{lang === 'id' ? 'Aplikasi akan disembunyikan dari semua pengguna di perusahaan ini. Data Anda tetap tersimpan dan akan muncul kembali saat diinstal ulang.' : 'The app will be hidden from everyone in this company. Your data is kept and reappears when you reinstall.'}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="module-uninstall-cancel">{lang === 'id' ? 'Batal' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-destructive hover:bg-destructive/90" onClick={() => { const m = confirm; setConfirm(null); run(m, 'uninstall'); }} data-testid="module-uninstall-confirm">{lang === 'id' ? 'Ya, hapus' : 'Yes, uninstall'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
